import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { E55Sample } from './types';

// Interface for creating a new sample
export interface CreateE55SampleRequest extends Partial<E55Sample> {
  fileId: number; // Required field for new sample creation
}

/**
 * Hook for adding a new E55 sample
 * 
 * @returns Mutation for creating a new E55 sample
 */
export const useAddE55Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (newSample: CreateE55SampleRequest) => {
      const { fileId, ...rest } = newSample;
      const payload = {
        ...rest,
        e55FileId: fileId,
      };
      
      const response = await axios.post('/data-sample/e55', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e55Samples', variables.fileId] 
      });
    },
  });
};

/**
 * Hook for updating an existing E55 sample
 * 
 * @returns Mutation for updating an E55 sample
 */
export const useUpdateE55Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (updatedSample: E55Sample) => {
      const { fileId, ...rest } = updatedSample as any;
      const payload = {
        ...rest,
        e55FileId: fileId,
      };
      
      const response = await axios.put('/data-sample/e55', payload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e55Samples'] 
      });
    },
  });
};

/**
 * Hook for deleting an E55 sample or amendment
 * 
 * @returns Mutation for deleting an E55 sample
 */
export const useDeleteE55Sample = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ sampleId, fileId }: { sampleId: number; fileId: number }) => {
      const response = await axios.delete(`/data-sample/e55`, {
        params: {
          sampleId,
          dataSetId: fileId,
        },
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refetch the updated data
      queryClient.invalidateQueries({ 
        queryKey: ['e55Samples', variables.fileId] 
      });
    },
  });
}; 